import {
  RoomAudioRenderer,
  useChat,
  useParticipants,
  useRemoteParticipants,
  useRoomContext,
  useStartAudio,
} from '@livekit/components-react';
import type { JoinResponse } from '@hiredude/meet-shared';
import { Volume2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { BrandMark } from '@/components/BrandMark';
import { ChatPanel, type DisplayChatMessage } from '@/components/call/ChatPanel';
import { ControlBar } from '@/components/call/ControlBar';
import { PeoplePanel } from '@/components/call/PeoplePanel';
import { ShareDialog } from '@/components/call/ShareDialog';
import { VideoGrid } from '@/components/call/VideoGrid';
import { WaitingForOthers } from '@/components/call/WaitingForOthers';
import { NetworkStatus } from '@/components/NetworkStatus';
import { ReconnectOverlay } from '@/components/ReconnectOverlay';
import { RecordingBadge } from '@/components/RecordingBadge';
import { Button } from '@/components/ui/button';
import { useConnectionState } from '@/hooks/useConnectionState';
import { useElapsed } from '@/hooks/useElapsed';
import { useIstClock } from '@/hooks/useIstClock';
import { useFullscreen } from '@/hooks/useFullscreen';
import { useLowBandwidthGuard } from '@/hooks/useLowBandwidthGuard';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useWakeLock } from '@/hooks/useWakeLock';
import { fetchChatHistory, persistChatMessage } from '@/lib/api';

type Panel = 'people' | 'chat' | null;

/**
 * Everything inside the connected room. Must be rendered as a child of
 * LiveKitRoom, because it reads the room off the context.
 */
export function CallStage({
  session,
  otherPartyName,
  code,
  joinKey,
  onLeave,
}: {
  session: JoinResponse;
  otherPartyName: string;
  code: string;
  joinKey: string;
  onLeave: (reason: 'left' | 'ended') => void;
}) {
  const room = useRoomContext();
  const participants = useParticipants();
  const alone = useRemoteParticipants().length === 0;

  // One panel at a time. Side by side they would squeeze the video to nothing
  // on a laptop, and on a phone the second would simply cover the first.
  const [panel, setPanel] = useState<Panel>(null);
  const [sharing, setSharing] = useState(false);

  const { reconnecting } = useConnectionState(room);
  const { videoPausedForBandwidth, quality } = useLowBandwidthGuard(room, true);
  const elapsed = useElapsed();
  const wallClock = useIstClock(true);
  useWakeLock(true);

  // Fullscreen belongs to the stage, not to the video inside it: a fullscreen
  // element paints only its own subtree, so anything outside — the control bar,
  // the filmstrip — would vanish for as long as the share was fullscreen.
  const stageRef = useRef<HTMLDivElement>(null);
  const fullscreen = useFullscreen(stageRef);

  // A phone held sideways has ~360px of height to spend. The header is the
  // first thing to give it back, because the video is the point.
  const short = useMediaQuery('(max-height: 480px)');

  // Owned here, not in ChatPanel: the panel unmounts when closed, and a fresh
  // `useChat` starts with an empty history, so the messages that raised the
  // unread badge would be gone by the time anyone opened it.
  const { chatMessages, send: sendChat, isSending } = useChat();
  const persistedIds = useRef(new Set<string>());
  const [history, setHistory] = useState<DisplayChatMessage[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetchChatHistory(code, joinKey)
      .then((result) => {
        if (cancelled) return;
        const rows = (result.messages || []).map((row) => {
          const id = row.sourceId || row.id;
          persistedIds.current.add(id);
          return {
            id,
            timestamp: new Date(row.at).getTime(),
            message: row.text,
            from: { identity: row.identity, name: row.name },
          } satisfies DisplayChatMessage;
        });
        setHistory(rows);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [code, joinKey]);

  useEffect(() => {
    chatMessages.forEach((message) => {
      const text = String(message.message || '').trim();
      if (!text) return;
      const identity = message.from?.identity || session.identity;
      const sourceId = String(message.id || `${identity}:${message.timestamp}:${text}`).slice(0, 120);
      if (persistedIds.current.has(sourceId)) return;
      persistedIds.current.add(sourceId);
      persistChatMessage(code, joinKey, {
        text,
        at: new Date(message.timestamp).toISOString(),
        sourceId,
        fromIdentity: identity,
        fromName: message.from?.name || undefined,
      }).catch(() => {
        persistedIds.current.delete(sourceId);
      });
    });
  }, [chatMessages, code, joinKey, session.identity]);

  const displayMessages = useMemo(() => {
    const byId = new Map<string, DisplayChatMessage>();
    history.forEach((message) => byId.set(message.id, message));
    chatMessages.forEach((message) => {
      const identity = message.from?.identity || session.identity;
      const id = String(message.id || `${identity}:${message.timestamp}:${message.message}`).slice(0, 120);
      byId.set(id, {
        id,
        timestamp: message.timestamp,
        message: message.message,
        from: { identity, name: message.from?.name },
      });
    });
    return [...byId.values()].sort((a, b) => a.timestamp - b.timestamp);
  }, [history, chatMessages, session.identity]);

  const persistSend = useCallback(
    (text: string) => sendChat(text),
    [sendChat],
  );
  const [seenMessages, setSeenMessages] = useState(0);
  const unreadCount = Math.max(0, displayMessages.length - seenMessages);

  useEffect(() => {
    if (history.length) setSeenMessages((current) => Math.max(current, history.length));
  }, [history.length]);

  // Reading the panel counts as reading the messages, including ones that
  // arrive while it is already open.
  useEffect(() => {
    if (panel === 'chat') setSeenMessages(displayMessages.length);
  }, [panel, displayMessages.length]);

  // Safari and Chrome both refuse to play remote audio until the page has been
  // interacted with. Without this prompt the call is silent and looks broken.
  const { canPlayAudio, mergedProps } = useStartAudio({ room, props: {} });

  const togglePanel = useCallback((next: Exclude<Panel, null>) => {
    setPanel((current) => (current === next ? null : next));
  }, []);

  return (
    <div
      className="lk-video-conference call-shell flex h-full flex-col"
      style={{
        // Respect the notch and the home indicator on iPhones.
        paddingTop: 'env(safe-area-inset-top)',
        paddingRight: 'env(safe-area-inset-right)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
      }}
    >
      <header
        className={`call-header flex shrink-0 items-center justify-between gap-3 px-3 sm:px-4 ${
          short ? 'py-1' : 'py-2.5'
        }`}
      >
        <div className="call-title flex min-w-0 items-center gap-2.5 sm:gap-3">
          {!short && <BrandMark size="sm" showWordmark={false} className="shrink-0" />}
          <div className="min-w-0">
            <strong className="block truncate text-sm font-semibold sm:text-[15px]">
              {session.title}
            </strong>
            {!short && (
              <span className="text-muted-foreground text-xs tabular-nums">
                {elapsed}
                <span className="text-muted-foreground/70"> · {wallClock}</span>
              </span>
            )}
          </div>
          <RecordingBadge expected={session.recordingEnabled} />
        </div>

        <div className="call-header-actions flex shrink-0 items-center gap-2">
          <NetworkStatus quality={quality} videoPaused={videoPausedForBandwidth} />
        </div>
      </header>

      <div className={`call-body relative flex min-h-0 flex-1${panel ? ' has-panel' : ''}`}>
        <div ref={stageRef} className="call-stage relative min-w-0 flex-1">
          <VideoGrid compactControls={short} fullscreen={fullscreen} />

          {(alone || !canPlayAudio) && (
            // Stacked rather than overlapping, and click-through except for
            // the sound button, so neither hides the other or the tiles.
            <div className="pointer-events-none absolute inset-x-0 top-3 z-20 flex flex-col items-center gap-2 px-3">
              {alone && <WaitingForOthers otherPartyName={otherPartyName} />}
              {!canPlayAudio && (
                <Button
                  {...mergedProps}
                  size="sm"
                  className="pointer-events-auto shadow-[var(--shadow-lift)]"
                >
                  <Volume2 />
                  Tap to enable sound
                </Button>
              )}
            </div>
          )}

          {/* The bar floats over the video rather than reserving a strip of
              its own: on a phone in landscape that strip is most of the
              usable height. */}
          <div
            className={`pointer-events-none absolute inset-x-0 bottom-0 z-20 flex justify-center px-2 sm:px-4 ${
              short ? 'pb-1.5' : 'pb-3 sm:pb-4'
            }`}
          >
            <ControlBar
              peopleOpen={panel === 'people'}
              peopleCount={participants.length}
              onTogglePeople={() => togglePanel('people')}
              chatOpen={panel === 'chat'}
              unreadCount={unreadCount}
              onToggleChat={() => togglePanel('chat')}
              canShare={session.role === 'host'}
              onShare={() => setSharing(true)}
              onLeave={() => void room.disconnect()}
            />
          </div>
        </div>

        {panel === 'people' && (
          <PeoplePanel
            code={code}
            joinKey={joinKey}
            isHost={session.role === 'host'}
            onClose={() => setPanel(null)}
            onEnded={() => onLeave('ended')}
          />
        )}

        {panel === 'chat' && (
          <ChatPanel
            messages={displayMessages}
            onSend={persistSend}
            isSending={isSending}
            onClose={() => setPanel(null)}
          />
        )}
      </div>

      {session.role === 'host' && (
        <ShareDialog open={sharing} onOpenChange={setSharing} code={code} joinKey={joinKey} />
      )}

      {/* Remote audio has to be attached to a real element for Safari to play
          it; the grid renders video only. */}
      <RoomAudioRenderer />
      <ReconnectOverlay visible={reconnecting} />
    </div>
  );
}
